import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import { Link } from "react-router-dom";
import "header-pattern/dist/main-ds1-stopgap.css";
import "./Header.css";
import { LinkList, Navigation } from "../../config/headerNavigations";

/** Utility to make unique, stable ids for ARIA attributes */
const uid = (() => {
  let i = 0;
  return (prefix = "id") => `${prefix}-${++i}`;
})();

const NavDropdownMenu: React.FC<Navigation> = ({ title, linkList }) => {
  const [expanded, setExpanded] = useState(false);
  const buttonId = useMemo(() => uid("head-nav-dropmenu-btn"), []);
  const listId = useMemo(() => uid("head-nav-dropmenu-list"), []);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const hasLinks = Array.isArray(linkList) && linkList.length > 0;

  const toggle = useCallback(() => setExpanded((prev) => !prev), []);
  const close = useCallback(() => setExpanded(false), []);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        close();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [close]);

  // Keyboard interactions
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      switch (e.key) {
        case "Enter":
        case " ":
        case "ArrowDown": {
          e.preventDefault();
          if (!expanded) setExpanded(true);
          const firstLink = document.querySelector<HTMLAnchorElement>(
            `#${listId} a`,
          );
          firstLink?.focus();
          break;
        }
        case "Escape": {
          e.preventDefault();
          close();
          const btn = document.getElementById(buttonId);
          (btn as HTMLButtonElement | null)?.focus();
          break;
        }
      }
    },
    [expanded, listId, buttonId, close],
  );

  const onListKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        const btn = document.getElementById(buttonId);
        (btn as HTMLButtonElement | null)?.focus();
      }
    },
    [buttonId, close],
  );

  return (
    <div
      ref={containerRef}
      className="drop-menu-pattern"
      data-select-label="selected"
      data-list-type="link"
    >
      <div className="drop-menu">
        <button
          className={`drop-menu-dropdown ${expanded ? "--active" : ""}`}
          id={buttonId}
          name={buttonId}
          aria-expanded={hasLinks ? expanded : undefined}
          aria-haspopup={hasLinks ? "menu" : undefined}
          aria-controls={hasLinks ? listId : undefined}
          onClick={hasLinks ? toggle : undefined}
          onKeyDown={hasLinks ? onKeyDown : undefined}
          type="button"
        >
          <span className="drop-menu-button-text">{title}</span>
          {hasLinks && (
            <img
              className="drop-down-menu-chevron ml-2!"
              src="/assets/images/chevrondown-white.svg"
              alt=""
              width="12"
              height="12"
              aria-hidden="true"
            />
          )}
        </button>

        {hasLinks && expanded && (
          <ul
            className="drop-menu-list -expanded"
            id={listId}
            aria-labelledby={buttonId}
          >
            {linkList.map((linkItem: LinkList, index: number) => {
              const uniqueKey = `menuitem-${index}-${linkItem.link || linkItem.linkText}`;
              return (
                <li className="drop-menu-list-op p-0!" role="none" key={uniqueKey}>
                  {/* Prefer Link for client-side routing; fall back to anchor if linkItem.href is external */}
                  {linkItem.link?.startsWith("http") ? (
                    <a
                      className="drop-menu-list-link"
                      role="menuitem"
                      href={linkItem.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onKeyDown={onListKeyDown}
                    >
                      <span className="drop-menu-list-text">
                        {linkItem.linkText}
                      </span>
                    </a>
                  ) : (
                    <Link
                      className="drop-menu-list-link w-full py-3! px-6!"
                      role="menuitem"
                      onClick={toggle}
                      to={linkItem.link ?? "/"}
                      onKeyDown={onListKeyDown}
                    >
                      <span className="drop-menu-list-text">
                        {linkItem.linkText}
                      </span>
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default NavDropdownMenu;
